import { LendApiPrequalTab } from '../constants';
import { FooterCardIconType, FooterCardItem } from './components/FooterCard/FooterCard';

export type MilestoneName = 'Boat Info' | 'About You' | 'Results' | 'Confirm';

export interface MilestoneConfig<T extends string = string> {
  name: string;
  tabs: Set<T>;
  shouldHide?: (tab: T) => boolean;
}

export interface ValuePropConfig {
  items: FooterCardItem[];
  primaryIcon: FooterCardIconType;
  title: string;
}

export interface TabConfig {
  milestoneIndex: number;
  isStartOfGroup: boolean;
  valueProp?: ValuePropConfig;
}

export interface LendAPIProgressConfig<T extends string = string> {
  defaultValueProp?: ValuePropConfig;
  milestones: MilestoneConfig<T>[];
  tabLookup: Map<T, TabConfig>;
  tabsWithNoProgressBar?: Set<T>;
  tabsWithNoValueProp?: Set<T>;
}

export interface PrequalProgressConfig extends LendAPIProgressConfig<LendApiPrequalTab> {
  milestones: MilestoneConfig<LendApiPrequalTab>[];
  defaultValueProp: ValuePropConfig;
  tabLookup: Map<LendApiPrequalTab, TabConfig>;
}

const DEFAULT_VALUE_PROP_CONFIG_SECURITY: ValuePropConfig = {
  items: [
    { text: "We'll encrypt your information to keep it secure." },
    { text: 'privacy-policy' },
    { text: 'Zero down loans offered for boats up to $100,000' },
  ],
  primaryIcon: 'dollarSign',
  title: 'Your one-stop shop for boat loans',
};

const DEFAULT_VALUE_PROP_CONFIG_TRUST: ValuePropConfig = {
  items: [
    { text: "We'll compare top lenders to find the most competitive rates for you" },
    { text: "Quick turnaround. Once approved we'll finalize your loan within 24 hours" },
    { text: 'Zero-down options available for boats up to $100,000' },
  ],
  primaryIcon: 'shield',
  title: 'A smarter, faster way to finance your boat',
};

export const TABS_THAT_SHOULD_ALWAYS_SHOW_HEADER = new Set([LendApiPrequalTab.DECLINED]);
export const PREQUAL_PROGRESS_CONFIG: PrequalProgressConfig = {
  milestones: [
    {
      name: 'Boat Info',
      tabs: new Set([
        LendApiPrequalTab.LETS_GET_STARTED,
        LendApiPrequalTab.FINANCING_DETAIL,
        LendApiPrequalTab.DOWN_PAYMENT,
      ]),
    },
    {
      name: 'About You',
      tabs: new Set([LendApiPrequalTab.ABOUT_YOU, LendApiPrequalTab.SELECT_FICO]),
    },
    {
      name: 'Results',
      tabs: new Set([
        LendApiPrequalTab.RESULTS,
        LendApiPrequalTab.QUALIFIED,
        LendApiPrequalTab.UNQUALIFIED,
      ]),
    },
  ],
  tabLookup: new Map([
    [
      LendApiPrequalTab.LETS_GET_STARTED,
      { milestoneIndex: 0, isStartOfGroup: true, valueProp: DEFAULT_VALUE_PROP_CONFIG_SECURITY },
    ],
    [
      LendApiPrequalTab.FINANCING_DETAIL,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
    [
      LendApiPrequalTab.DOWN_PAYMENT,
      { milestoneIndex: 0, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_SECURITY },
    ],
    [
      LendApiPrequalTab.ABOUT_YOU,
      { milestoneIndex: 1, isStartOfGroup: true, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
    [
      LendApiPrequalTab.SELECT_FICO,
      { milestoneIndex: 1, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
    [
      LendApiPrequalTab.RESULTS,
      { milestoneIndex: 2, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
    [
      LendApiPrequalTab.QUALIFIED,
      { milestoneIndex: 2, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
    [
      LendApiPrequalTab.UNQUALIFIED,
      { milestoneIndex: 2, isStartOfGroup: false, valueProp: DEFAULT_VALUE_PROP_CONFIG_TRUST },
    ],
  ]),
  defaultValueProp: DEFAULT_VALUE_PROP_CONFIG_SECURITY,
};

const findMilestoneIndexForTab = <T extends string>(
  tab: T,
  config: LendAPIProgressConfig<T>
): number => config.milestones.findIndex((milestone) => milestone.tabs.has(tab));

export const getCurrentMilestoneIndex = <T extends string>(
  tab: T,
  config: LendAPIProgressConfig<T>,
  lastKnownTab?: T
): number => {
  const milestoneIndexForTab = findMilestoneIndexForTab(tab, config);
  if (milestoneIndexForTab !== -1) {
    return milestoneIndexForTab;
  }

  if (lastKnownTab) {
    const lastKnownMilestoneIndex = findMilestoneIndexForTab(lastKnownTab, config);
    if (lastKnownMilestoneIndex !== -1) {
      return lastKnownMilestoneIndex;
    }
  }

  if (config.milestones.length > 0) {
    return 0;
  }

  return -1;
};

export const shouldShowProgressBar = <T extends string>(
  tab: T | undefined,
  config: LendAPIProgressConfig<T>
): boolean => {
  if (!tab) return false;
  return !config.tabsWithNoProgressBar?.has(tab);
};
